package app.nestbridge.feature.invitations.presentation

import androidx.compose.runtime.Composable

import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import app.nestbridge.domain.identity.usecase.GetCurrentUserUseCase
import app.nestbridge.feature.careteam.usecase.FindAllCareTeamsUseCase
import app.nestbridge.feature.invitations.model.Invitation
import app.nestbridge.feature.invitations.model.InvitationStatus
import app.nestbridge.feature.invitations.usecase.AcceptInvitationUseCase
import app.nestbridge.feature.invitations.usecase.DeclineInvitationUseCase
import app.nestbridge.feature.invitations.usecase.FindReceivedInvitationsUseCase
import kotlinx.coroutines.launch
import app.nestbridge.app.util.launchSafely
import app.nestbridge.feature.invitations.screen.InvitationListEvent
import app.nestbridge.feature.invitations.screen.InvitationListResult
import app.nestbridge.feature.invitations.screen.InvitationListState
import app.nestbridge.feature.invitations.screen.InvitationListScreen
import app.nestbridge.feature.invitations.screen.InvitationDetailScreen
import app.nestbridge.feature.invitations.screen.InvitationQrScanScreen
import app.nestbridge.feature.invitations.screen.InvitationResult
import app.nestbridge.feature.invitations.screen.InvitationListEvent.EnterCode
import app.nestbridge.feature.invitations.screen.InvitationListEvent.ScanQr
import app.nestbridge.feature.invitations.screen.InvitationListEvent.DecideLater
import com.slack.circuit.codegen.annotations.CircuitInject
import com.slack.circuit.foundation.rememberAnsweringNavigator
import com.slack.circuit.runtime.Navigator
import com.slack.circuit.runtime.presenter.Presenter
import me.tatarka.inject.annotations.Assisted
import me.tatarka.inject.annotations.Inject
import software.amazon.lastmile.kotlin.inject.anvil.AppScope

@CircuitInject(
    screen = InvitationListScreen::class,
    scope = AppScope::class,
)
@Inject
class InvitationListPresenter(
    @Assisted private val screen: InvitationListScreen,
    @Assisted private val navigator: Navigator,
    private val findReceivedInvitationsUseCase: FindReceivedInvitationsUseCase,
    private val acceptInvitationUseCase: AcceptInvitationUseCase,
    private val declineInvitationUseCase: DeclineInvitationUseCase,
    private val findAllCareTeamsUseCase: FindAllCareTeamsUseCase,
    private val getCurrentUserUseCase: GetCurrentUserUseCase,
) : Presenter<InvitationListState> {

    @Composable
    override fun present(): InvitationListState {
        val scope = rememberCoroutineScope()
        var invitations by remember { mutableStateOf<List<Invitation>>(emptyList()) }
        var isUserPartOfAnyTeam by remember { mutableStateOf(false) }

        suspend fun refresh() {
            invitations = findReceivedInvitationsUseCase()
                .getOrElse { emptyList() }
                .filter { it.status == InvitationStatus.PENDING }

            // Check if user is part of any team
            val userId = getCurrentUserUseCase()?.id
            if (userId != null) {
                val allTeams = findAllCareTeamsUseCase()
                isUserPartOfAnyTeam = allTeams.any { team ->
                    team.members.any { it.userId == userId }
                }
            }

            if (invitations.isEmpty()) {
                navigator.pop(
                    InvitationListResult(
                        status = InvitationListResult.Status.NoInvitations,
                    ),
                )
            }
        }

        val detailNavigator = rememberAnsweringNavigator<InvitationResult>(navigator) { result ->
            scope.launchSafely {
                val invitation = invitations.find { it.id == result.invitationId }
                if (invitation != null) {
                    when (result.decision) {
                        InvitationResult.Decision.Accepted -> acceptInvitationUseCase(invitation.id)
                        InvitationResult.Decision.Declined -> declineInvitationUseCase(invitation.id)
                        InvitationResult.Decision.Cancelled -> return@launchSafely
                    }
                }
                refresh()
            }
        }

        fun onEvent(event: InvitationListEvent) {
            when (event) {
                InvitationListEvent.OnAppear -> scope.launchSafely { refresh() }
                is InvitationListEvent.OpenInvitation -> {
                    detailNavigator.goTo(
                        InvitationDetailScreen(
                            invitationId = event.invitationId,
                        ),
                    )
                }
                EnterCode -> navigator.pop(
                    InvitationListResult(
                        status = InvitationListResult.Status.Completed,
                    ),
                )
                ScanQr -> navigator.goTo(InvitationQrScanScreen())
                DecideLater -> navigator.pop(
                    InvitationListResult(
                        status = InvitationListResult.Status.Skipped,
                    ),
                )
            }
        }

        return InvitationListState(
            invitations = invitations,
            isUserPartOfAnyTeam = isUserPartOfAnyTeam,
            eventSink = ::onEvent,
        )
    }
}
